// ---------------------------------------------------------------------------
// The browser side of the console's data endpoints.
//
// SAME SHAPE AS api/auth.ts AND FOR THE SAME REASONS: pure request
// construction, no React, no DOM, no colour — so every property below is
// assertable under `node --test` with no component-test stack.
//
// THE CSRF AND SURFACE HEADERS ARE BUILT IN ONE PLACE. V4 found the cost of
// getting this wrong: the CSRF header made every mutating request non-simple,
// the browser preflighted it, the preflight did not name the header and the
// POST was dropped before it left the machine. Nothing was logged and 132 tests
// were green. verify-V5.sh reads the header names out of THIS FILE and requires
// the live preflight to admit each one, so a header added here and not to the
// policy fails in the script rather than in somebody's browser.
//
// NO TAXONOMY. There is no list of roles here, no list of client roles and no
// status vocabulary — they come from GET /v1/reference/roles, which reads them
// from the database. Absolute rule 4 applied to the front end: the day somebody
// adds a role, a hard-coded <select> goes on offering the old ones and nobody
// finds out until a customer asks.
// ---------------------------------------------------------------------------

import { CSRF_HEADER, CSRF_HEADER_VALUE, type RequestSpec } from "./auth";
import type { PayRule, ReadinessBlocker, RoleDef } from "../system/wire-types";
export type { PayRule, ReadinessBlocker, RoleDef } from "../system/wire-types";

export type Client = {
  id: string;
  name: string;
  reference: string | null;
  /** Tour T-15. The next event not yet over and not cancelled; optional, older API builds omit it. */
  next_event?: { id: string; name: string; starts_at: string } | null;
  status: string;
  /** 0194. The account state's words and tone, and the one move from it. */
  status_label: string;
  status_tone: string;
  move: { status: string; label: string; done: string } | null;
  portal_enabled: boolean;
  billing_email: string | null;
  billing_address: string | null;
  vat_number: string | null;
  payment_terms_days: number;
  purchase_order_required: boolean;
  created_at: string;
  updated_at: string;
};

export type ClientUser = {
  id: string;
  user_account_id: string;
  role: string;
  status: string;
  status_label: string;
  status_tone: string;
  is_withdrawn: boolean;
  created_at: string;
};

export type Member = {
  id: string;
  user_account_id: string;
  role: string;
  /** The role's own label, and the person's email (0320; null when the caller may not read it). */
  role_label?: string;
  email?: string | null;
  /** Tour T-20: only when the organisation shows names (0325). */
  display_name?: string | null;
  status: string;
  /** 0185. The state's words and tone, and the one move an administrator may make from it. */
  status_label: string;
  status_tone: string;
  move: { status: string; label: string; done: string } | null;
  is_self: boolean;
  created_at: string;
};

export type Worker = {
  id: string;
  given_name: string;
  family_name: string;
  display_name: string;
  reference: string | null;
  email: string | null;
  phone: string | null;
  home_postcode: string | null;
  home_locality: string | null;
  home_country: string | null;
  employment_type?: string;
  /** Only the detail read, and only to the person or whoever may manage workers. */
  date_of_birth?: string | null;
  status: string;
  /** WHETHER a bench row is claimed, never by whom. The API sends no account id. */
  has_account: boolean;
  /**
   * Sweep S5, S41 (0193): only the detail view sends it. The state's words, tone and
   * the invite button's verb (null when there is nothing to invite) are its row's.
   */
  sign_in?: { state: string; label: string; tone: string; invite_label: string | null } | null;
  created_at: string;
  updated_at: string;
};

/**
 * Sweep 3a. One step of the console's first-run list.
 *
 * `screen` is an id from system/screens.ts and `params` fills its `:id`, so the
 * address is derived from the route table here rather than spelled by the API.
 * `who` is set only when the viewer cannot act: it says who can.
 */
export type GettingStartedStep = {
  key: string;
  title: string;
  explanation: string;
  done: boolean;
  optional: boolean;
  screen: string;
  params: { id: string } | null;
  can_act: boolean;
  who: string | null;
  /** R8. The reader's own chore, offered as a button rather than a link. */
  action: "resend_email_confirmation" | null;
};

/** `show` is the server's decision: not complete, not put away, and something here is yours to do. */
/** Tour T-11. Four counts for the Overview's Today strip; see api/src/routes/today.ts. */
export type Today = { events_today: number; unfilled: number; not_arrived: number; to_approve: number };

export type GettingStarted = {
  steps: GettingStartedStep[];
  required_done: number;
  required_total: number;
  complete: boolean;
  dismissed: boolean;
  show: boolean;
  /** R8 (0259). The signed-in person's own address, where the confirm step's resend goes. */
  resend_to: string | null;
};

/**
 * The bench lifecycle, read from app.worker_status.
 *
 * `is_assignable` travels with it so a screen can say what a status MEANS
 * without a second list in the browser deciding. There is deliberately no
 * default here and none derived from `rank`: rank is a display order, not a
 * capability ordering, and V5's invite form is the worked example of what
 * happens when the two are confused.
 */
export type WorkerStatusOption = {
  id: string;
  label: string;
  rank: number;
  is_assignable: boolean;
  description: string;
};

/** V7. A credential the organisation requires, or a platform template. */
export type CredentialType = {
  id: string;
  key: string;
  name: string;
  /** Tour T-34: the name in other languages, {"hu": "…"}; absent for an older server. */
  names?: Record<string, string>;
  validity_months: number | null;
  is_blocking: boolean;
  is_statutory: boolean;
  warn_days: number;
  required_verification_method: string;
  document_required: boolean;
  is_retired: boolean;
  is_template: boolean;
  created_at: string;
  updated_at: string;
};

/**
 * V7. One row of a worker's credential HISTORY.
 *
 * `is_current` comes from the server rather than being derived from
 * `valid_to === null` in a component. The temporal design has exactly one
 * definition of "current" and it is `valid_to is null` in Postgres; a second
 * one in the browser is a second thing to get wrong.
 */
export type WorkerCredential = {
  id: string;
  worker_id: string;
  credential_type_id: string;
  reference: string | null;
  issued_on: string | null;
  expires_on: string | null;
  valid_from: string;
  valid_to: string | null;
  superseded_by: string | null;
  is_current: boolean;
  verification_method: string;
  verified_at: string | null;
  is_verified_by_person: boolean;
  document_id: string | null;
  created_at: string;
};

export type VerbunkDocument = {
  id: string;
  worker_id: string | null;
  kind: string;
  credential_type_id: string | null;
  filename: string;
  content_type: string;
  byte_size: number;
  uploaded_at: string;
  retention_class: string;
  delete_after: string | null;
};

/**
 * V8. A role definition, as the list endpoint returns it.
 *
 * MOVED HERE FROM screens/Roles.tsx BY THE DEMO GALLERY, and the move is worth
 * a sentence. 10-demo-gallery.md §3 requires the gallery to render the same
 * fixtures the tests use, and a fixture is only worth anything if it is typed
 * against the contract it claims to imitate. A response shape declared inside
 * the component that consumes it cannot be — the fixture would have to restate
 * it, and a restated type agrees with the original exactly until the day it
 * does not, silently, which is the whole failure mode a shared fixture module
 * exists to close.
 */

/** V8. One credential a role definition requires. */
export type RoleRequirement = {
  id: string;
  credential_type_id: string;
  credential_name: string;
  is_blocking: boolean;
  is_statutory: boolean;
  is_template: boolean;
};

/** V7. What a document may be filed as. Read from the database, never listed here. */
export type DocumentKindOption = {
  id: string;
  label: string;
  requires_credential_type: boolean;
  description: string;
};

/**
 * V7. How a credential may be verified.
 *
 * Structurally a `MethodFacts` from system/credentials.ts plus its words, and
 * deliberately not written as an intersection with it: this file is the API's
 * shapes and system/ is the product's rules, and pointing the first at the
 * second makes the request layer depend on the reasoning layer.
 */
export type VerificationMethodOption = {
  id: string;
  satisfies_blocking: boolean;
  label: string;
  description: string;
};

export type RoleOption = {
  id: string;
  label: string;
  is_external?: boolean;
  /** Verbunk staff (0041) and the scheduled-jobs member (0269): label rows, never offered. */
  is_platform?: boolean;
  is_robot?: boolean;
  may_approve?: boolean;
  rank: number;
  description: string;
};

/**
 * V11. How badly a requirement is needed.
 *
 * `level` IS A NUMBER AND THE WORDS COME WITH IT. 12-assignment-spine's board
 * view model types criticality `1|2|3|4|5`, so the number is the contract — but
 * a screen that turned that digit into its own word would be holding a
 * taxonomy, and the two flags are properties no browser could derive from a
 * digit at all. The words are deliberately not written here — verify-V11.sh §7
 * greps the source for them, and a type comment is source.
 */
export type CriticalityLevel = {
  level: number;
  label: string;
  counts_toward_readiness: boolean;
  blocks_opening: boolean;
  description: string;
};

/** V11. What a charge rate is per. `multiplies_by_hours` is the arithmetic, as data. */
export type RateBasis = {
  id: string;
  label: string;
  rank: number;
  multiplies_by_hours: boolean;
  description: string;
};

/**
 * V11. A pay rule set the organisation could pin.
 *
 * `is_pinnable` TRAVELS AND THE UNPINNABLE ONES ARE STILL IN THE LIST. A rule
 * set sitting in an unpublished version is the single most likely reason a
 * coordinator cannot find the one they want, and a filtered list cannot say so.
 */
export type PayRuleSetOption = {
  id: string;
  key: string;
  name: string;
  is_platform: boolean;
  config_pack_version_id: string;
  config_pack_name: string;
  config_pack_version: number;
  config_pack_version_status: string;
  config_pack_version_status_label: string;
  is_pinnable: boolean;
  /** Sweep S10: what this set prices — the worker's pay or the client's charge. */
  basis: string;
};

/** V11. The atomic unit of demand, as the list endpoint returns it. */
export type Requirement = {
  id: string;
  event_id: string;
  event_name: string;
  event_accepts_planning: boolean;

  role_def_id: string;
  role_name: string;
  role_is_retired: boolean;

  starts_at: string;
  ends_at: string;
  headcount: number;

  criticality: number;
  criticality_label: string;
  criticality_counts_toward_readiness: boolean;
  criticality_blocks_opening: boolean;

  zone: string | null;

  pay_rule_set_id: string;
  /** Sweep S10. What the client is charged against; null means not invoiceable. */
  charge_rule_set_id: string | null;
  charge_rule_set_name: string | null;
  /** S27b. How people check in on this post (null: hours are stated), and whether it can still change. */
  checkin_policy_id?: string | null;
  checkin_policy_name?: string | null;
  checkin_policy_locked?: boolean;
  pay_rule_set_name: string;
  pay_rule_set_key: string;
  pay_config_pack_name: string;
  pay_config_pack_version: number;
  pay_config_pack_version_is_pinnable: boolean;

  /** MINOR units, always. Never divided anywhere but at the render. */
  charge_rate_minor: number;
  charge_currency: string;
  charge_rate_basis: string;
  charge_rate_basis_label: string;
  charge_rate_basis_multiplies_by_hours: boolean;

  break_minutes: number;
  break_is_paid: boolean;

  /** S110 (0229). The signed assessment's controls are not in the post's latest briefing: nobody can be briefed or check in until it is published. */
  safety_controls_unpublished: boolean;

  created_at: string;
};

/**
 * V11. Two requirements on one post whose windows touch.
 *
 * THE SENTENCE IS ASSEMBLED ON THE SERVER, which is absolute rule 6 applied to
 * a detection rather than to a decision: the screen and any later notification
 * must not be able to drift apart about what was found.
 */
export type RequirementOverlap = {
  requirement_ids: string[];
  zone: string;
  overlap_starts: string;
  overlap_ends: string;
  overlap_minutes: number;
  sentence: string;
  /** S145. The template and values the sentence was filled from, for the catalogue. */
  sentence_key?: string;
  params?: Record<string, string | number>;
};

/**
 * A mutating request. Every one carries the CSRF header, because the server
 * refuses a mutating request without it — see api/src/auth/csrf.ts §4.3.
 */
/**
 * EXPORTED AT V24 SO api/portal.ts CAN SPEND THEM.
 *
 * The client portal is a separate namespace by 18-client-portal.md §6.3 and its
 * request specs live in their own file, but "separate namespace" is a statement
 * about the API surface, not about how a header gets attached. Two copies of the
 * CSRF header construction is how one of them stops matching the preflight the
 * V5 script reads out of THIS file.
 */
export function mutate(
  method: "POST" | "PATCH" | "DELETE",
  base: string,
  path: string,
  payload?: unknown,
): RequestSpec {
  const headers: Record<string, string> = { [CSRF_HEADER]: CSRF_HEADER_VALUE };
  const spec: RequestSpec = {
    method,
    url: `${base}${path}`,
    headers,
    credentials: "include",
  };
  if (payload !== undefined) {
    headers["Content-Type"] = "application/json";
    // EVERY VALUE TRAVELS IN THE BODY, NEVER IN THE URL. A query string reaches
    // browser history, every proxy log and the next request's Referer.
    spec.body = JSON.stringify(payload);
  }
  return spec;
}

export function read(base: string, path: string): RequestSpec {
  return { method: "GET", url: `${base}${path}`, headers: {}, credentials: "include" };
}

export const console_ = {
  roles: (base: string) => read(base, "/v1/reference/roles"),

  listClients: (base: string, opts: { archived?: boolean; q?: string; cursor?: string; limit?: number } = {}) => {
    // Sweep S15: search and a cursor, so the list does not stop at 50.
    // The flag goes in as an object key: V9's guard refuses the bare string of a pack state
    // in any file that also names config packs, and this client parameter shares the word.
    const params = new URLSearchParams(opts.archived ? { archived: "true" } : {});
    if (opts.q) params.set("q", opts.q);
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit) params.set("limit", String(opts.limit));
    const query = params.toString();
    return read(base, `/v1/clients${query ? `?${query}` : ""}`);
  },
  getClient: (base: string, id: string) => read(base, `/v1/clients/${encodeURIComponent(id)}`),
  createClient: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/clients", payload),
  updateClient: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/clients/${encodeURIComponent(id)}`, payload),
  inviteClientUser: (base: string, id: string, email: string, role: string) =>
    mutate("POST", base, `/v1/clients/${encodeURIComponent(id)}/users`, { email, role }),
  setClientUserRole: (base: string, id: string, clientUserId: string, role: string) =>
    mutate("PATCH", base, `/v1/clients/${encodeURIComponent(id)}/users/${encodeURIComponent(clientUserId)}`, { role }),
  revokeClientUser: (base: string, id: string, clientUserId: string) =>
    mutate("DELETE", base, `/v1/clients/${encodeURIComponent(id)}/users/${encodeURIComponent(clientUserId)}`),

  workerStatuses: (base: string) => read(base, "/v1/reference/worker-statuses"),
  employmentTypes: (base: string) => read(base, "/v1/reference/employment-types"),
  mySubjectRequests: (base: string) => read(base, "/v1/privacy/my-requests"),
  setHomeArea: (base: string, id: string, payload: { home_postcode: string | null; home_locality: string | null }) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(id)}/home-area`, payload),

  // ---- V12. AVAILABILITY --------------------------------------------------
  getAvailability: (base: string, workerId: string) =>
    read(base, `/v1/workers/${encodeURIComponent(workerId)}/availability`),
  /**
   * NO DATES BY DEFAULT, AND THAT IS THE POINT. The endpoint defaults the period
   * in SQL, so no screen computes "now plus four weeks" in JavaScript — the
   * class of arithmetic V12 exists to keep out of the browser.
   */
  getAvailabilityWindows: (base: string, workerId: string, from?: string, to?: string) =>
    read(
      base,
      `/v1/workers/${encodeURIComponent(workerId)}/availability/windows` +
        (from && to ? `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}` : ""),
    ),
  addAvailabilityPattern: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/availability/patterns`, payload),
  removeAvailabilityPattern: (base: string, patternId: string) =>
    mutate("DELETE", base, `/v1/availability/patterns/${encodeURIComponent(patternId)}`),
  addAvailabilityException: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/availability/exceptions`, payload),
  removeAvailabilityException: (base: string, exceptionId: string) =>
    mutate("DELETE", base, `/v1/availability/exceptions/${encodeURIComponent(exceptionId)}`),
  setAvailabilityPreference: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/workers/${encodeURIComponent(workerId)}/availability/preference`, payload),
  /**
   * THE FLYWHEEL SWITCH. No worker id, because there is no worker id an agency
   * could substitute — 16-the-flywheel.md §4.1.
   */
  setMarketplaceVisibility: (base: string, visible: boolean) =>
    mutate("PATCH", base, "/v1/me/marketplace-visibility", { visible }),

  // ---- SWEEP §4. THE RICHER PROFILE (0179) — one set for console and app ----
  getWorkerProfile: (base: string, workerId: string) =>
    read(base, `/v1/workers/${encodeURIComponent(workerId)}/profile`),
  setWorkerLanguages: (base: string, workerId: string, languages: { language: string; level: string }[]) =>
    mutate("PATCH", base, `/v1/workers/${encodeURIComponent(workerId)}/languages`, { languages }),
  setWorkerSkills: (base: string, workerId: string, skills: string[]) =>
    mutate("PATCH", base, `/v1/workers/${encodeURIComponent(workerId)}/skills`, { skills }),
  /** R14 / 0266. The agency's own skill list. */
  createSkillTag: (base: string, label: string) => mutate("POST", base, "/v1/skill-tags", { label }),
  retireSkillTag: (base: string, id: string) =>
    mutate("POST", base, `/v1/skill-tags/${encodeURIComponent(id)}/retirement`, {}),
  setEmergencyContact: (base: string, workerId: string, contact: { name: string; relationship: string; phone: string }) =>
    mutate("PATCH", base, `/v1/workers/${encodeURIComponent(workerId)}/emergency-contact`, contact),
  removeEmergencyContact: (base: string, workerId: string) =>
    mutate("DELETE", base, `/v1/workers/${encodeURIComponent(workerId)}/emergency-contact`),

  /**
   * The bench.
   *
   * THE SEARCH TERM IS IN THE QUERY STRING AND THAT IS A DELIBERATE EXCEPTION to
   * the rule two functions above ("every value travels in the body"). A GET has
   * no body, and making the bench search a POST to keep a name out of a URL
   * would make it uncacheable, unlinkable and un-back-buttonable for no gain —
   * the term is a fragment of a colleague's name that the person typing already
   * knows. The rule exists for secrets and credentials; this is neither.
   */
  listWorkers: (
    base: string,
    opts: { q?: string; status?: string; cursor?: string; limit?: number } = {},
  ) => {
    const params = new URLSearchParams();
    if (opts.q) params.set("q", opts.q);
    if (opts.status) params.set("status", opts.status);
    if (opts.cursor) params.set("cursor", opts.cursor);
    if (opts.limit) params.set("limit", String(opts.limit));
    const query = params.toString();
    return read(base, `/v1/workers${query ? `?${query}` : ""}`);
  },
  getWorker: (base: string, id: string) => read(base, `/v1/workers/${encodeURIComponent(id)}`),
  /** The signed-in person's own bench row, if they have one. Names no id. */
  myWorker: (base: string) => read(base, "/v1/workers/me"),
  // Sweep S22. Current credentials that run out within N days, across the bench.
  expiringCredentials: (base: string, withinDays: number) =>
    read(base, `/v1/credentials/expiring?within_days=${withinDays}`),
  // Sweep S14. A client's change requests, and the agency's answer.
  eventChangeRequests: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/change-requests`),
  resolveChangeRequest: (base: string, id: string, status: string) =>
    mutate("POST", base, `/v1/change-requests/${encodeURIComponent(id)}/resolution`, { status }),
  // Sweep §4. The incident log: read and record per event, close once with a note.
  eventIncidents: (base: string, eventId: string) => read(base, `/v1/events/${encodeURIComponent(eventId)}/incidents`),
  recordIncident: (base: string, eventId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/incidents`, payload),
  resolveIncident: (base: string, id: string, note: string) =>
    mutate("POST", base, `/v1/incidents/${encodeURIComponent(id)}/resolution`, { note }),
  /** R15b / 0249. Remove a bystander's name from an incident's text. */
  redactIncidentName: (base: string, id: string, name: string) =>
    mutate("POST", base, `/v1/incidents/${encodeURIComponent(id)}/redactions`, { name }),
  // Sweep §4 / 0221–0223. H&S risk assessments: read and draft per event, sign off a version.
  eventRiskAssessments: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/risk-assessments`),
  saveRiskAssessment: (base: string, eventId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/risk-assessments`, payload),
  signOffRiskAssessment: (base: string, id: string) =>
    mutate("POST", base, `/v1/risk-assessments/${encodeURIComponent(id)}/sign-off`, {}),
  // Sweep §4 / 0234–0236. API keys and webhooks; each secret is in its create response only.
  apiKeys: (base: string) => read(base, "/v1/api-keys"),
  createApiKey: (base: string, name: string, scopes: string[]) => mutate("POST", base, "/v1/api-keys", { name, scopes }),
  revokeApiKey: (base: string, id: string) => mutate("DELETE", base, `/v1/api-keys/${encodeURIComponent(id)}`),
  webhooks: (base: string) => read(base, "/v1/webhooks"),
  registerWebhook: (base: string, url: string, eventKinds: string[]) =>
    mutate("POST", base, "/v1/webhooks", { url, event_kinds: eventKinds }),
  revokeWebhook: (base: string, id: string) => mutate("DELETE", base, `/v1/webhooks/${encodeURIComponent(id)}`),
  drainWebhooks: (base: string) => mutate("POST", base, "/v1/webhooks/drain", {}),
  // Sweep §4 / 0215. The applicant inbox and the public link it is fed by.
  applications: (base: string) => read(base, "/v1/applications"),
  openApplicationLink: (base: string) => mutate("POST", base, "/v1/application-link"),
  closeApplicationLink: (base: string) => mutate("DELETE", base, "/v1/application-link"),
  acceptApplication: (base: string, id: string) =>
    mutate("POST", base, `/v1/applications/${encodeURIComponent(id)}/acceptance`),
  declineApplication: (base: string, id: string, reason: string) =>
    mutate("POST", base, `/v1/applications/${encodeURIComponent(id)}/decline`, { reason }),
  eraseApplication: (base: string, id: string) => mutate("DELETE", base, `/v1/applications/${encodeURIComponent(id)}`),
  // Sweep §4 / 0199–0200. Messages to the crew, read receipts, and reminders that
  // are queued by a person until the owner rules who else may queue them.
  eventMessages: (base: string, eventId: string) => read(base, `/v1/events/${encodeURIComponent(eventId)}/messages`),
  sendCrewMessage: (base: string, eventId: string, payload: { body: string; requirement_id: string | null }) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/messages`, payload),
  queueReminders: (base: string, eventId: string) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/reminders`, {}),
  reminderSetting: (base: string) => read(base, "/v1/shift-reminders"),
  updateReminderSetting: (base: string, hoursBefore: number | null) =>
    mutate("PATCH", base, "/v1/shift-reminders", { hours_before: hoursBefore }),
  myShiftMessages: (base: string, id: string) => read(base, `/v1/my/shifts/${encodeURIComponent(id)}/messages`),
  openMyShiftMessages: (base: string, id: string) =>
    mutate("POST", base, `/v1/my/shifts/${encodeURIComponent(id)}/messages/read`, {}),
  // P4-C10. The plan, bought on Stripe's Checkout and managed on its portal.
  billing: (base: string) => read(base, "/v1/billing"),
  startCheckout: (base: string, plan: string, buyerKind: string, taxNumber: string | null = null) =>
    mutate("POST", base, "/v1/billing/checkout", { plan, buyer_kind: buyerKind, ...(taxNumber ? { tax_number: taxNumber } : {}) }),
  openBillingPortal: (base: string) => mutate("POST", base, "/v1/billing/portal", {}),
  // D88-24. The hub's PDF of the organisation's own subscription invoice, or its credit note.
  billingPdf: (base: string, ref: string, which: "pdf" | "credit-pdf") =>
    read(base, `/v1/billing/invoices/${encodeURIComponent(ref)}/${which}`),
  // Sweep S20. The organisation's own name, timezone and currency.
  updateOrganisation: (base: string, payload: Record<string, unknown>) => mutate("PATCH", base, "/v1/org", payload),
  // Sweep 3a / 0246. The first-run list, every tick read from the organisation's rows.
  // Tour T-11. The Overview's Today strip.
  today: (base: string) => read(base, "/v1/org/today"),
  gettingStarted: (base: string) => read(base, "/v1/org/getting-started"),
  setGettingStartedDismissed: (base: string, dismissed: boolean) =>
    mutate("PATCH", base, "/v1/org/getting-started", { dismissed }),
  // Sweep S5. Invites the record's own email; the body is empty on purpose.
  inviteWorker: (base: string, workerId: string) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/invite`, {}),
  createWorker: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/workers", payload),
  updateWorker: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/workers/${encodeURIComponent(id)}`, payload),

  // ---- V7: credentials and documents --------------------------------------
  listCredentialTypes: (base: string, opts: { includeRetired?: boolean } = {}) =>
    read(base, `/v1/credential-types${opts.includeRetired ? "?include_retired=true" : ""}`),
  createCredentialType: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/credential-types", payload),
  updateCredentialType: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/credential-types/${encodeURIComponent(id)}`, payload),

  listWorkerCredentials: (base: string, workerId: string) =>
    read(base, `/v1/workers/${encodeURIComponent(workerId)}/credentials`),
  /**
   * Record OR renew — ONE call, and the server decides which it was.
   *
   * There is deliberately no `renewCredential` beside this. A caller that chose
   * "record" for somebody who already holds the credential would hit the partial
   * unique index and get a conflict they cannot act on; the database already
   * knows whether a current row exists. The response reports `renewed` so the
   * screen can say which happened.
   */
  recordCredential: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/credentials`, payload),
  /**
   * Close a credential with no successor. NOT a delete, and there is no delete
   * to offer: the row stays so a Proof Pack for an event that happened while it
   * was open still resolves it.
   */
  withdrawCredential: (base: string, workerId: string, credentialId: string) =>
    mutate(
      "POST",
      base,
      `/v1/workers/${encodeURIComponent(workerId)}/credentials/${encodeURIComponent(credentialId)}/withdraw`,
    ),

  // ---- V8: role definitions, the gate, assignments and waivers -------------
  listRoleDefs: (base: string, opts: { includeRetired?: boolean; includeHidden?: boolean } = {}) => {
    const q = [opts.includeRetired ? "include_retired=true" : "", opts.includeHidden ? "include_hidden=true" : ""].filter(Boolean);
    return read(base, `/v1/role-defs${q.length ? `?${q.join("&")}` : ""}`);
  },
  // D88-40. A system role in or out of this workspace's pickers.
  setRoleDefHidden: (base: string, id: string, hidden: boolean) =>
    mutate("PATCH", base, `/v1/role-defs/${encodeURIComponent(id)}/visibility`, { hidden }),
  createRoleDef: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/role-defs", payload),
  updateRoleDef: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/role-defs/${encodeURIComponent(id)}`, payload),
  listRoleRequirements: (base: string, id: string) =>
    read(base, `/v1/role-defs/${encodeURIComponent(id)}/requirements`),
  addRoleRequirement: (base: string, id: string, credentialTypeId: string) =>
    mutate("POST", base, `/v1/role-defs/${encodeURIComponent(id)}/requirements`, {
      credential_type_id: credentialTypeId,
    }),
  removeRoleRequirement: (base: string, id: string, requirementId: string) =>
    mutate(
      "DELETE",
      base,
      `/v1/role-defs/${encodeURIComponent(id)}/requirements/${encodeURIComponent(requirementId)}`,
    ),

  /**
   * THE GATE'S VERDICT, BEFORE THE ATTEMPT.
   *
   * The four parameters are in the query string and every one of them is
   * required — including `ends_at`, which is the whole of 09-... §V8.2. There is
   * no overload that omits it and defaults to the start, because that default is
   * exactly the defect: a licence valid on the day somebody is rostered and
   * lapsed by the day they work. The server refuses the call without it, and
   * this signature refuses to make it.
   */
  assignmentGate: (
    base: string,
    q: {
      workerId: string;
      roleDefId: string;
      startsAt: string;
      endsAt: string;
      /**
       * V31a. Hours of work in the shift, where the scheduler knows.
       *
       * OMITTED WHEN UNKNOWN, never sent as the elapsed span. `0095` measures a
       * day a shift crosses only when somebody has recorded the figure, and a
       * client that helpfully filled it in would be inventing the number the
       * working-time ceiling then refuses on.
       */
      hoursWorked?: number | null;
    },
  ) => {
    const params = new URLSearchParams({
      worker_id: q.workerId,
      role_def_id: q.roleDefId,
      starts_at: q.startsAt,
      ends_at: q.endsAt,
    });
    if (q.hoursWorked !== null && q.hoursWorked !== undefined) {
      params.set("hours_worked", String(q.hoursWorked));
    }
    return read(base, `/v1/assignments/gate?${params.toString()}`);
  },

  listAssignments: (base: string, opts: { workerId?: string } = {}) =>
    read(base, `/v1/assignments${opts.workerId ? `?worker_id=${encodeURIComponent(opts.workerId)}` : ""}`),
  createAssignment: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/assignments", payload),

  // ---- V16: the worker's own surface ----------------------------------------
  /** V36. Geocoding a venue, and the SEPARATE act of trusting the answer. */
  geocoders: (base: string) => read(base, "/v1/geocoders"),
  geocodeVenue: (base: string, eventId: string, provider: string) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/geocode`, { provider }),
  promoteVenuePoint: (base: string, eventId: string) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/venue-point`),

  /** V37. The worker's own subscribable shift calendar. */
  issueCalendarFeed: (base: string) => mutate("POST", base, "/v1/my/calendar"),
  revokeCalendarFeed: (base: string) => mutate("DELETE", base, "/v1/my/calendar"),

  myShifts: (base: string) => read(base, "/v1/my/shifts"),
  myShift: (base: string, id: string) => read(base, `/v1/my/shifts/${encodeURIComponent(id)}`),
  // D12b. The crew of the post this booking leads; 403 for anyone who does not lead it.
  leadCrew: (base: string, id: string) => read(base, `/v1/my/shifts/${encodeURIComponent(id)}/crew`),
  marketplacePrompt: (base: string) => read(base, "/v1/my/marketplace-prompt"),
  answerMarketplacePrompt: (base: string, answer: string) =>
    mutate("POST", base, "/v1/my/marketplace-prompt", { answer }),

  // ---- V16a: identity verification ------------------------------------------
  myVerification: (base: string) => read(base, "/v1/my/verification"),
  startVerification: (base: string) => mutate("POST", base, "/v1/my/verification", {}),
  completeVerification: (base: string, sessionId: string) =>
    mutate("POST", base, "/v1/my/verification/complete", { sessionId }),
  verificationReviews: (base: string) => read(base, "/v1/verification-reviews"),
  resolveVerificationReview: (base: string, id: string, resolution: string) =>
    mutate("POST", base, `/v1/verification-reviews/${encodeURIComponent(id)}/resolution`, { resolution }),

  // ---- V17a: the platform review desk ---------------------------------------
  // A SEPARATE PREFIX, not a parameter on the two above. `/v1/platform/` is refused to
  // any actor who has an organisation at all, so it is a surface a tenant can never
  // reach rather than one they merely lack a permission for.
  platformReviews: (base: string) => read(base, "/v1/platform/verification-reviews"),
  verificationResolutions: (base: string) => read(base, "/v1/platform/verification-resolutions"),

  // ---- V18: the briefing -----------------------------------------------------
  requirementBriefing: (base: string, id: string) =>
    read(base, `/v1/requirements/${encodeURIComponent(id)}/briefing`),
  saveBriefingOverride: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/requirements/${encodeURIComponent(id)}/briefing/override`, payload),
  publishBriefing: (base: string, id: string, correction: boolean) =>
    mutate("POST", base, `/v1/requirements/${encodeURIComponent(id)}/briefing/publish`, { correction }),
  myBriefing: (base: string, assignmentId: string) =>
    read(base, `/v1/my/shifts/${encodeURIComponent(assignmentId)}/briefing`),
  acknowledgeBriefing: (base: string, assignmentId: string, versionId: string) =>
    mutate("POST", base, `/v1/my/shifts/${encodeURIComponent(assignmentId)}/briefing/acknowledge`, {
      briefing_version_id: versionId,
    }),
  // V19. The prompt is a separate read so the screen knows whether the policy needs a
  // location BEFORE asking the browser for one.
  checkinPrompt: (base: string, assignmentId: string) =>
    read(base, `/v1/my/shifts/${encodeURIComponent(assignmentId)}/checkin`),
  recordAttendance: (base: string, assignmentId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/my/shifts/${encodeURIComponent(assignmentId)}/attendance`, payload),
  attendanceKinds: (base: string) => read(base, "/v1/reference/attendance-kinds"),
  // Sweep S10: what a rule set can price.
  calculationBases: (base: string) => read(base, "/v1/reference/calculation-bases"),
  clockSources: (base: string) => read(base, "/v1/reference/clock-sources"),
  requirementAttendance: (base: string, id: string) =>
    read(base, `/v1/requirements/${encodeURIComponent(id)}/attendance`),
  // V20. The board is fetched whole; the change log takes the client's own last
  // occurred_at back as a cursor, so a three-second poll costs one filtered read.
  liveBoard: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/board`),
  // D12. The count per role and zone, the roll calls taken, and taking one.
  rollCallTally: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/roll-call`),
  rollCalls: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/roll-calls`),
  takeRollCall: (base: string, eventId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/roll-calls`, payload),
  changeLog: (base: string, eventId: string, since: string | null) =>
    read(
      base,
      `/v1/events/${encodeURIComponent(eventId)}/change-log` +
        (since ? `?since=${encodeURIComponent(since)}` : ""),
    ),
  // V21. ONE READ FOR BOTH ROLES. The supervisor countersigning and the agency approving
  // look at the same rows; what differs is which button is live, and the server answers
  // that per step rather than the screen guessing from a role name.
  eventTimesheets: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/timesheets`),
  submitTimesheet: (base: string, assignmentId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/assignments/${encodeURIComponent(assignmentId)}/timesheet`, payload),
  approveTimesheet: (base: string, timesheetId: string, step: string) =>
    mutate("POST", base, `/v1/timesheets/${encodeURIComponent(timesheetId)}/approvals`, { step }),
  timesheetCorrectionReasons: (base: string) =>
    read(base, "/v1/reference/timesheet-correction-reasons"),
  // ---- V22. THE PROOF PACK ------------------------------------------------
  eventProofPacks: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/proof-packs`),
  proofChain: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/proof-chain`),
  issueProofPack: (base: string, eventId: string, supersedes: string | null) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/proof-packs`,
      supersedes === null ? {} : { supersedes }),
  // ---- V23. PAY, AND THE PAYMENT BATCH ------------------------------------
  //
  // `computePay` TAKES THE BASIS AS A FIELD, not as two functions, for the reason the
  // approval step is a field: a vocabulary spelled here is a vocabulary that has to be
  // redeployed when the database gains a row.
  eventPay: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/pay`),
  timesheetPay: (base: string, timesheetId: string) =>
    read(base, `/v1/timesheets/${encodeURIComponent(timesheetId)}/pay`),
  computePay: (base: string, timesheetId: string, basis: string) =>
    mutate("POST", base, `/v1/timesheets/${encodeURIComponent(timesheetId)}/pay`, { basis }),
  paymentProviders: (base: string) => read(base, "/v1/payment-providers"),
  paymentBatches: (base: string) => read(base, "/v1/payment-batches"),
  paymentBatch: (base: string, batchId: string) =>
    read(base, `/v1/payment-batches/${encodeURIComponent(batchId)}`),
  // THE PROVIDER AND THE REFERENCE ARE THE SERVER'S BUSINESS AS LITTLE AS POSSIBLE, but
  // both have to come from somewhere. The provider is read from `paymentProviders` rather
  // than named here — absolute rule 4 — and the reference is generated by the caller so a
  // retry can carry the same one.
  runPaymentBatch: (base: string, provider: string, calculations: string[]) =>
    mutate("POST", base, "/v1/payment-batches", {
      provider,
      reference: `PAY-${Date.now()}`,
      calculations,
    }),
  resolvePlatformReview: (base: string, id: string, resolution: string) =>
    mutate("POST", base, `/v1/platform/verification-reviews/${encodeURIComponent(id)}/resolution`, {
      resolution,
    }),
  overrideReasons: (base: string) => read(base, "/v1/reference/override-reasons"),
  listOverrides: (base: string, workerId: string) =>
    read(base, `/v1/workers/${encodeURIComponent(workerId)}/credential-overrides`),
  grantOverride: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/credential-overrides`, payload),
  withdrawOverride: (base: string, id: string) =>
    mutate("POST", base, `/v1/credential-overrides/${encodeURIComponent(id)}/withdraw`, {}),

  uploadDocument: (base: string, workerId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/workers/${encodeURIComponent(workerId)}/documents`, payload),
  getDocument: (base: string, id: string) => read(base, `/v1/documents/${encodeURIComponent(id)}`),
  /**
   * The bytes. EVERY CALL TO THIS WRITES AN ACCESS-LOG ROW, server side, in the
   * same transaction that releases the key — so it must not be called to render
   * a list or to prefetch. The metadata endpoint above is the one for that.
   */
  // No purpose means the server's default; the purpose vocabulary is not spelled here.
  documentContent: (base: string, id: string, purpose?: string) =>
    read(base, `/v1/documents/${encodeURIComponent(id)}/content${purpose ? `?purpose=${encodeURIComponent(purpose)}` : ""}`),
  documentAccessLog: (base: string, id: string) =>
    read(base, `/v1/documents/${encodeURIComponent(id)}/access-log`),

  listMembers: (base: string) => read(base, "/v1/org/members"),
  inviteMember: (base: string, email: string, role: string, name?: string) =>
    mutate("POST", base, "/v1/org/members", { email, role, ...(name && name.trim() !== "" ? { name: name.trim() } : {}) }),
  updateMember: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/org/members/${encodeURIComponent(id)}`, payload),

  // ---- V9. CONFIGURATION PACKS ---------------------------------------------
  //
  // THREE REFERENCE CALLS BEFORE ANY SCREEN CODE, AND THAT IS THE SLICE. A
  // console with thirty verticals typed into a <select> is exactly the code
  // fork 03-work-verticals.md says no vertical requires — and it would be
  // invisible to a grep of api/src, which is where the taxonomy guard looks.
  verticals: (base: string) => read(base, "/v1/reference/verticals"),
  configPackStates: (base: string) => read(base, "/v1/reference/config-pack-states"),
  checkinMethods: (base: string) => read(base, "/v1/reference/checkin-methods"),

  listConfigPacks: (base: string, opts: { vertical?: string; templates?: boolean } = {}) => {
    const params = new URLSearchParams();
    if (opts.vertical) params.set("vertical", opts.vertical);
    if (opts.templates) params.set("templates", "true");
    const query = params.toString();
    return read(base, `/v1/config-packs${query ? `?${query}` : ""}`);
  },
  getConfigPack: (base: string, id: string) => read(base, `/v1/config-packs/${encodeURIComponent(id)}`),
  createConfigPack: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/config-packs", payload),
  renameConfigPack: (base: string, id: string, name: string) =>
    mutate("PATCH", base, `/v1/config-packs/${encodeURIComponent(id)}`, { name }),
  /** "Editing produces a new version" — the operation that makes freezing liveable. */
  createConfigPackVersion: (base: string, id: string, notes?: string) =>
    mutate("POST", base, `/v1/config-packs/${encodeURIComponent(id)}/versions`, notes ? { notes } : {}),
  getConfigPackVersion: (base: string, id: string) =>
    read(base, `/v1/config-pack-versions/${encodeURIComponent(id)}`),
  /**
   * Publish or archive. ONE CALL FOR BOTH, because they are the same act — a
   * move along app.config_pack_status — and the state id is passed through
   * rather than named, so a fourth state needs no edit here.
   */
  setConfigPackVersionState: (base: string, id: string, status: string) =>
    mutate("PATCH", base, `/v1/config-pack-versions/${encodeURIComponent(id)}`, { status }),
  discardConfigPackVersion: (base: string, id: string) =>
    mutate("DELETE", base, `/v1/config-pack-versions/${encodeURIComponent(id)}`),
  // Sweep S6: the rates themselves (app.pay_rule), which nothing wrote before.
  createPayRule: (base: string, setId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/pay-rule-sets/${encodeURIComponent(setId)}/rules`, payload),
  deletePayRule: (base: string, id: string) => mutate("DELETE", base, `/v1/pay-rules/${encodeURIComponent(id)}`),
  createPayRuleSet: (base: string, versionId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/config-pack-versions/${encodeURIComponent(versionId)}/pay-rule-sets`, payload),
  createCheckinPolicy: (base: string, versionId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/config-pack-versions/${encodeURIComponent(versionId)}/checkin-policies`, payload),

  // V10. The filters are ids from the vocabulary the caller was given, never
  // names spelled here — so a state added to app.event_status is filterable the
  // day it is inserted.
  listEvents: (
    base: string,
    filters: { status?: string; clientId?: string; q?: string; cursor?: string } = {},
  ) => {
    const q = new URLSearchParams();
    if (filters.status) q.set("status", filters.status);
    if (filters.clientId) q.set("client_id", filters.clientId);
    // Sweep S15: search and a cursor, so older events are reachable.
    if (filters.q) q.set("q", filters.q);
    if (filters.cursor) q.set("cursor", filters.cursor);
    const query = q.toString();
    return read(base, query ? `/v1/events?${query}` : "/v1/events");
  },
  getEvent: (base: string, id: string) => read(base, `/v1/events/${id}`),
  // V-ETA-2b / V-ETA-2a. event.clinic staffing requests: the claim (the grant crosses once),
  // the link to an event, and the quote priced by the server from that event's posts.
  hubClaims: (base: string) => read(base, "/v1/hub/claims"),
  claimHubRequests: (base: string, grant: string) => mutate("POST", base, "/v1/hub/claims", { grant_token: grant }),
  linkHubClaim: (base: string, engagement: string, eventId: string) =>
    mutate("POST", base, `/v1/hub/claims/${encodeURIComponent(engagement)}/link`, { event_id: eventId }),
  sendHubQuote: (base: string, eventId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/hub-quote`, payload),
  listEventStatuses: (base: string) => read(base, "/v1/reference/event-statuses"),
  createEvent: (base: string, payload: Record<string, unknown>) =>
    mutate("POST", base, "/v1/events", payload),
  updateEvent: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/events/${encodeURIComponent(id)}`, payload),
  // Sweep S49. A new event for `starts_on`, with this one's posts moved by the same days.
  copyEvent: (base: string, id: string, startsOn: string) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(id)}/copy`, { starts_on: startsOn }),

  // V11. The requirement builder. `criticality` and `charge_rate_basis` are
  // sent as values from the vocabularies the caller was given, never as words
  // spelled here.
  listRequirements: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/requirements`),
  createRequirement: (base: string, eventId: string, payload: Record<string, unknown>) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/requirements`, payload),
  updateRequirement: (base: string, id: string, payload: Record<string, unknown>) =>
    mutate("PATCH", base, `/v1/requirements/${encodeURIComponent(id)}`, payload),
  removeRequirement: (base: string, id: string) =>
    mutate("DELETE", base, `/v1/requirements/${encodeURIComponent(id)}`),
  listPayRuleSets: (base: string) => read(base, "/v1/pay-rule-sets"),
  listCriticalityLevels: (base: string) => read(base, "/v1/reference/criticality-levels"),
  listRateBases: (base: string) => read(base, "/v1/reference/rate-bases"),

  // V13. THE ASSIGNMENT SPINE. Every state id below is a value that came from
  // the server, never a word spelled here — which is why `transitionAssignment`
  // takes `toState` as an opaque string and this file contains no list of them.
  listAssignmentStates: (base: string) => read(base, "/v1/reference/assignment-states"),
  listEventCrew: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/assignments`),
  listAssignmentHistory: (base: string, id: string) =>
    read(base, `/v1/assignments/${encodeURIComponent(id)}/transitions`),
  /**
   * V14. THE ONE PLACE THE FILL FIGURE COMES FROM.
   *
   * The planning board reads this and never recomputes any part of it —
   * 12-… §V14.2. V17 replaces what the endpoint computes; nothing on this side
   * changes when it does, which is the entire reason it is an endpoint rather
   * than four lines in the board.
   */
  eventReadiness: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/readiness`),

  // V23a. ROSTER PUBLICATION. The list and the publication state arrive together
  // because the board's strip is one sentence made of both, and two requests
  // would let the half saying "there is something to publish" go stale.
  listRosterVersions: (base: string, eventId: string) =>
    read(base, `/v1/events/${encodeURIComponent(eventId)}/roster-versions`),
  publishRoster: (base: string, eventId: string, note: string | null) =>
    mutate("POST", base, `/v1/events/${encodeURIComponent(eventId)}/roster-versions`, note === null ? {} : { note }),
  /**
   * §2.2. THE KEY IS MINTED BY THE CALLER AND HELD ACROSS RETRIES.
   *
   * Minting one inside this function would look identical and guarantee
   * nothing: a retry would carry a fresh key and apply the change twice. It is
   * a required parameter for that reason, and the screen holds it for the
   * lifetime of the user's action rather than the lifetime of the request.
   */
  transitionAssignment: (
    base: string,
    id: string,
    payload: { to_state?: string | null; is_reserve?: boolean; reason?: string | null },
    idempotencyKey: string,
  ) =>
    mutate("POST", base, `/v1/assignments/${encodeURIComponent(id)}/transitions`, {
      ...payload,
      idempotency_key: idempotencyKey,
    }),
};

// ---- V13. THE ASSIGNMENT SPINE --------------------------------------------
// `status_is_terminal` and `status_fills_headcount` TRAVEL rather than being
// derived from the state name, for the reason stated above V12's block: a
// screen that compared a state id against a literal would be a second copy of
// the machine, and the copy is what stops agreeing.

export type AssignmentState = {
  id: string;
  label: string;
  rank: number;
  description: string;
  is_terminal: boolean;
  fills_headcount: boolean;
  locks_configuration: boolean;
};

/** One edge of the machine. The screen renders a button per entry, no more. */
export type AssignmentEdge = {
  from_state: string | null;
  to_state: string;
  to_label: string;
  required_permission: string | null;
  by_worker: boolean;
  by_system: boolean;
  description: string;
};

export type TransitionReason = { id: string; label: string; description: string };

export type AssignmentStateVocabulary = {
  states: AssignmentState[];
  transitions: AssignmentEdge[];
  reasons: TransitionReason[];
};

export type CrewAssignment = {
  id: string;
  worker_id: string;
  worker_name: string;
  role_def_id: string;
  role_name: string;
  requirement_id: string | null;
  requirement_zone: string | null;
  requirement_headcount: number | null;
  status: string;
  status_label: string;
  status_is_terminal: boolean;
  status_fills_headcount: boolean;
  is_reserve: boolean;
  starts_at: string;
  ends_at: string;
  updated_at: string;
};

// ---- V23a. ROSTER PUBLICATION ----------------------------------------------
// `superseded_by` IS COMPUTED BY THE SERVER, not stored — storing it would mean
// editing a published version, which is the one thing append-only forbids.

export type RosterVersion = {
  version_id: string;
  version_no: number;
  published_at: string;
  published_by: string | null;
  note: string | null;
  /** The denominator. The sum of every post's headcount at publication. */
  seat_count: number;
  /** The numerator. Seats a non-reserve booking held, capped at the headcount. */
  covered_count: number;
  /** Manifest rows, which exceeds `seat_count` whenever standbys exist. */
  item_count: number;
  supersedes_id: string | null;
  superseded_by: string | null;
  is_current: boolean;
};

export type RosterPublicationPayload = {
  has_unpublished_changes: boolean;
  current_version_id: string | null;
  may_publish: boolean;
  versions: RosterVersion[];
};

// ---- V14. READINESS, AS THE BOARD RECEIVES IT ------------------------------
// Every number below is DECIDED BY THE SERVER. Nothing on this side divides
// `filled` by `headcount`, and verify-V14.sh greps the board for an attempt.


/** V17. One line of the score's arithmetic. Sum these, get the score. */
export type ReadinessExplanation = {
  requirement_id: string;
  weight: number;
  readiness: number;
  /** weight x readiness. score = 100 x SUM contribution / weight_total. */
  contribution: number;
};

/** V17. A post that stops the event opening and has nobody on it at all. */
export type DarkPost = {
  requirement_id: string;
  place: string;
  criticality_label: string;
  headcount: number;
};

/** V17. One sample of the score, and any scope change recorded against it. */
export type ReadinessSample = {
  at: string;
  score: number | null;
  /** Server-assembled and names no person — see the route's note on rule 5. */
  annotation: string | null;
};

export type RequirementReadiness = {
  requirement_id: string;
  headcount: number;
  filled: number;
  reserves: number;
  shortfall: number;
  criticality: number;
  criticality_label: string;
  counts_toward_readiness: boolean;
  blocks_opening: boolean;
  /**
   * 0 to 1, and ITS MEANING CHANGED AT V17 WITHOUT THIS FIELD BEING RENAMED.
   *
   * It was `filled / headcount` — raw coverage. It is now this post's weighted
   * readiness: bookings counted by how firm they are and how close the shift is,
   * standby at a discount and capped. What it is a figure OF is stated by `basis`
   * on the envelope, which is why that sentence comes from the server.
   */
  contribution: number;
  blockers: ReadinessBlocker[];
};

export type EventReadiness = {
  event: { id: string; name: string };
  /**
   * WHAT THE NUMBER ACTUALLY IS, in words, from the server.
   *
   * Printed on the board. Until V17 it says the figure is raw coverage rather
   * than the Readiness Score — and a screen that showed a percentage without
   * saying which of the two it was would be the misreading this whole slice is
   * built to avoid.
   */
  basis: string;
  /** null when no requirement counts toward readiness. Never 0 — see the route. */
  overall: number | null;
  counted_requirements: number;
  requirements: RequirementReadiness[];

  // -------------------------------------------------------------------------
  // V17. Everything below arrived with the Readiness Score.
  // -------------------------------------------------------------------------

  /**
   * 0 to 100, null when nothing counts. THE SAME NUMBER AS `overall`, which is
   * the fraction form V14's board was built against. The server derives one from
   * the other in a single line, so they are two presentations rather than two
   * opinions — but a screen should read exactly one of them, and new screens read
   * this one.
   */
  score: number | null;
  computed_at: string;
  /** The denominator the score is measured against. §V17.4.3's retained figure. */
  weight_total: number;
  critical_posts: { total: number; covered: number; dark: DarkPost[] };
  /** Keyed by state id, so a state added later needs no change here. */
  by_state: Record<string, number>;
  /**
   * §V17.5: not optional. Any number shown to a client must be defensible line by
   * line, and under 06-compliance.md §1 a figure affecting worker assignment has
   * to be explainable anyway.
   */
  explanation: ReadinessExplanation[];
  /** Every blocker on the event, ranked by what fixing it would move. */
  blockers: ReadinessBlocker[];
  trend: ReadinessSample[];
};

// ---------------------------------------------------------------------------
// V16. The worker's own surface.
// ---------------------------------------------------------------------------

/** One shift, as the person working it needs to read it. */
export type MyShift = {
  id: string;
  status: string;
  status_label: string;
  status_is_terminal: boolean;
  status_fills_headcount: boolean;
  is_reserve: boolean;
  starts_at: string;
  ends_at: string;
  /**
   * WHEN TO ARRIVE, already resolved by the server from an offset on the
   * requirement. NULL means arrive when the shift starts — and the screen says
   * that in words rather than repeating the start time, because two times on one
   * card is the thing somebody misreads in a car park.
   */
  call_at: string | null;
  role_name: string;
  zone: string | null;
  event_id: string | null;
  event_name: string | null;
  /** Zone, venue, address and postcode, joined by the server. Null if none exist. */
  meeting_point: string | null;
  break_minutes: number | null;
  break_is_paid: boolean | null;
};

/**
 * A move this worker may make from where the shift is now.
 *
 * FROM THE DATABASE'S OWN TABLE, never decided here. The worker app holds no copy
 * of the state machine, for the same reason V14's board does not: a screen that
 * chose its own buttons would be a second machine, and the day the two disagreed
 * the button would be the one telling the lie.
 */
export type MyShiftMove = {
  to_state: string;
  /** The destination STATE's name. Right for a chip, wrong for a button. */
  to_label: string;
  /**
   * What the button says — imperative, from 0033. `to_label` gave the worker app
   * two buttons reading "Accepted" and "Declined", which are not things a person
   * can do; they are things that will have happened.
   */
  action_label: string;
  description: string;
};

/**
 * V16a, per 17-identity-verification.md. WHAT THE WORKER IS TOLD, AND NO MORE:
 * whether a check is needed, what it is called, and whether one is with a person.
 * There is no provider status here and no reason a check failed — we are never told
 * one, and a field for it would eventually be filled.
 */
export type VerificationState = {
  requiredTier: string | null;
  requiredTierLabel: string | null;
  holdsTier: string | null;
  needsCheck: boolean;
  inReview: boolean;
};

/**
 * V17a. One person waiting for a human to look at their identity check.
 *
 * NO COST AND NO PROVIDER REFERENCE — the projection in
 * `app.platform_review_queue()` carries neither, so there is nothing here to leak. The
 * worker's NAME is present because a reviewer is about to make a decision about a
 * person, and doing that from a uuid is how people get treated as records.
 */
export type PlatformReview = {
  id: string;
  openedAt: string;
  organisation: string;
  workerName: string;
  tier: string;
  tierLabel: string;
  /** How many times they tried to take work and were turned away. */
  attempts: number;
};

/**
 * V18. One published briefing, exactly as it stood when it was published.
 *
 * THE SNAPSHOT AND NOT THE SOURCES. `meeting_point` here is the resolved merge of a pack
 * template and this event's override, frozen — so a screen rendering it cannot
 * accidentally show a worker something the version does not say.
 */
export type BriefingVersion = {
  id: string;
  version_no: number;
  material_version_no: number;
  is_correction: boolean;
  published_at: string;
  meeting_point: string | null;
  dress_code: string | null;
  duties: string | null;
  contacts: string | null;
  map_url: string | null;
  /** 0222. The signed risk assessment's hazards and controls for this post, one per line. */
  safety_controls?: string | null;
};

/** V16, per 16-the-flywheel.md §6a. */
export type MarketplacePrompt = {
  /** False for a person with no bench record — a scheduler is exactly that. */
  applicable: boolean;
  /** Whether to ask now: a shift is finished, and no recent answer stands. */
  due: boolean;
  visible: boolean | null;
  answer: string | null;
  answered_at?: string | null;
};

export type AssignmentTransition = {
  seq: string;
  from_state: string | null;
  from_label: string | null;
  to_state: string;
  to_label: string;
  from_is_reserve: boolean | null;
  to_is_reserve: boolean;
  /**
   * WHO, AS AN ID, for `EventTransition.by_user_id`'s reason: app_rw holds no
   * privilege on the identity tables, so the API has no name to send.
   */
  actor_user_account_id: string | null;
  actor_role: string | null;
  reason_code: string | null;
  reason_label: string | null;
  occurred_at: string;
};

// ---------------------------------------------------------------------------
// V9's row shapes. `is_editable` and `is_pinnable` TRAVEL FROM THE DATABASE and
// are not derived from the state name anywhere in this codebase — a screen that
// compared the status field against a literal state name to decide whether to
// disable a button would be a second copy of the rule, and the fourth state
// would arrive with the wrong affordances and no test failing.
//
// (The sentence above originally quoted such a comparison verbatim, and
// verify-V9.sh's own guard refused the file for it. Sixth occurrence of a
// comment about a rule tripping the rule — V3's finding 4, V4's finding 3,
// V7's 11, V8's 6, V9's 6. There is no prose exemption and there must not be.)
// ---------------------------------------------------------------------------

// ---- V12. AVAILABILITY ----------------------------------------------------
// EVERY DERIVED FACT ARRIVES DERIVED. `crosses_midnight` and `minutes` are
// computed on the server, in the zone's own rules, because the browser has no
// IANA arithmetic and the naive version is wrong on two days a year.
export type AvailabilityPattern = {
  id: string;
  worker_id: string;
  time_zone: string;
  /** 0 is Monday. Sent and received as a JSON array; the column stores a mask. */
  weekdays: number[];
  starts_local: string;
  ends_local: string;
  crosses_midnight: boolean;
  effective_from: string;
  effective_to: string | null;
  created_at: string;
};

export type AvailabilityExceptionKind = {
  id: string;
  label: string;
  /** The only question a screen asks of a kind. Never compare the id. */
  makes_available: boolean;
  description: string;
};

export type AvailabilityException = {
  id: string;
  worker_id: string;
  kind: string;
  kind_label: string;
  kind_makes_available: boolean;
  time_zone: string;
  on_date: string;
  starts_local: string | null;
  ends_local: string | null;
  is_whole_day: boolean;
  created_at: string;
};

/** Four nullable numbers. Null is "not stated", which is neither zero nor unlimited. */
export type AvailabilityPreference = {
  max_hours_per_week: number | null;
  max_shifts_per_week: number | null;
  min_notice_hours: number | null;
  max_travel_minutes: number | null;
  updated_at: string;
};

export type AvailabilityWindow = { starts_at: string; ends_at: string; minutes: number };

export type AvailabilityPayload = {
  worker: { id: string; given_name: string; family_name: string; marketplace_visible: boolean };
  patterns: AvailabilityPattern[];
  exceptions: AvailabilityException[];
  preference: AvailabilityPreference | null;
  exception_kinds: AvailabilityExceptionKind[];
};

/** Sweep §4 / 0179. The `may_*` flags are the server's answer; no screen derives them. */
export type WorkerProfile = {
  worker: { id: string; given_name: string; family_name: string };
  /** R14 (0266). `language` is an ISO 639-1 code; its words come with it. */
  languages: { language: string; language_label: string; level: string; level_label: string }[];
  /** R14. Tags from the agency's own list. */
  skills: { id: string; label: string }[];
  /** Free text recorded before the list existed that matched none of it; gone on the next save. */
  unmapped_languages: string[];
  languages_available: { id: string; label: string }[];
  /** The agency's open tags, offered only to whoever may tag people. */
  skill_tags: { id: string; label: string }[];
  emergency_contact: {
    name: string;
    relationship: string;
    relationship_label: string;
    phone: string;
    updated_at: string;
  } | null;
  emergency_contact_visible: boolean;
  may_edit_languages: boolean;
  may_edit_skills: boolean;
  may_edit_contact: boolean;
  language_levels: { id: string; label: string; description: string }[];
  relationships: { id: string; label: string }[];
};

export type Vertical = { id: string; label: string; tier: number; rank: number; description: string };

export type ConfigPackState = {
  id: string;
  label: string;
  rank: number;
  is_editable: boolean;
  is_pinnable: boolean;
  description: string;
};

export type CheckinMethodOption = {
  id: string;
  label: string;
  rank: number;
  requires_geofence: boolean;
  description: string;
};

export type ConfigPack = {
  id: string;
  vertical: string;
  vertical_label: string;
  key: string;
  name: string;
  /** Tour T-46: the name in the reader's language for a platform pack. */
  display_name?: string;
  is_template: boolean;
  forked_from_version_id: string | null;
  version_count: number;
  published_count: number;
  latest_published_version_id: string | null;
  latest_published_version: number | null;
};

export type ConfigPackVersion = {
  id: string;
  config_pack_id: string;
  version: number;
  status: string;
  status_label: string;
  is_editable: boolean;
  is_pinnable: boolean;
  published_at: string | null;
  archived_at: string | null;
  notes: string | null;
};

/** Sweep S6. One row of app.pay_rule, as the version view returns it. */

export type ConfigPackVersionDetail = ConfigPackVersion & {
  role_defs: { id: string; key: string; name: string; display_name?: string; is_retired: boolean; requirement_count: number }[];
  credential_types: {
    id: string;
    key: string;
    name: string;
    is_blocking: boolean;
    is_statutory: boolean;
    required_verification_method: string;
    is_fork: boolean;
  }[];
  pay_rule_sets: { id: string; key: string; name: string; rules: unknown; basis: string; pay_rules: PayRule[] }[];
  checkin_policies: {
    id: string;
    key: string;
    name: string;
    method: string;
    method_label: string;
    geofence_metres: number | null;
    tolerance_minutes: number;
  }[];
  pinned_event_count: number;
};

export type VerbunkEvent = {
  id: string;
  name: string;
  /** Tour T-37: places on the crew, and how many are filled; only the list carries them. */
  seats?: number;
  seats_filled?: number;
  reference: string | null;
  starts_at: string;
  ends_at: string;
  // THE STATE ARRIVES WITH ITS PROPERTIES. A screen given only `status` has to
  // decide what a state means, and the only way to do that in a browser is to
  // compare against a literal — absolute rule 4 broken inside a disabled-button
  // expression, where nobody looks.
  status: string;
  status_label: string;
  status_is_terminal: boolean;
  status_accepts_planning: boolean;
  status_requires_venue: boolean;
  client_account_id: string;
  client_name: string;
  venue_name: string | null;
  venue_lat?: string | null;
  venue_lon?: string | null;
  geocode_precision?: string | null;
  geocode_precision_label?: string | null;
  geocode_fit_for_fence?: boolean | null;
  geocode_provider?: string | null;
  geocode_promoted_at?: string | null;
  venue_address: string | null;
  venue_postcode: string | null;
  budget_minor: number | null;
  budget_currency: string | null;
  config_pack_id: string;
  config_pack_name: string;
  config_pack_version_id: string;
  config_pack_version: number;
  config_pack_version_status: string;
  config_pack_version_status_label: string;
  config_pack_version_is_pinnable: boolean;
};

/** One move an event has already made. The sentence is composed server-side. */
export type EventTransition = {
  from_status: string | null;
  from_label: string | null;
  to_status: string;
  to_label: string;
  at: string;
  /**
   * WHO, AS AN ID, BECAUSE THE CONSOLE CANNOT LEARN A NAME.
   *
   * app_rw holds no privilege on the identity tables, so the API has no name to
   * send — the same limit routes/org.ts records for the member list. The
   * timeline therefore shows WHEN and WHAT and does not pretend to show WHO.
   */
  by_user_id: string | null;
  sentence: string;
  /** S145. The template and values the sentence was filled from, for the catalogue. */
  sentence_key?: string;
  params?: Record<string, string | number>;
};

/**
 * A move this event may make NEXT, from app.event_status_transition.
 *
 * The console renders one button per entry and nothing else. Computing this
 * list in the browser would mean the browser holding a copy of the state
 * machine, and a copy is a thing that stops agreeing.
 */
export type AvailableTransition = { cancels?: boolean; status: string; label: string; action_label: string; requires_venue: boolean };

export type EventStatus = {
  id: string;
  label: string;
  rank: number;
  is_initial: boolean;
  is_terminal: boolean;
  accepts_planning: boolean;
  requires_venue: boolean;
  description: string;
  transitions_to: string[];
};
